import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Plus, Pencil, Trash2, Power, RotateCcw, Wallet, ClipboardList, Target, Stethoscope, RefreshCw, Download, AlertTriangle, FileHeart,
} from 'lucide-react';
import api from '../../../utils/api';
import { useConfirm } from '../../../components/ConfirmDialog';
import {
  Panel, Pill, RowMenu, DataTable, Empty, Facts, Meter, LoadingBlock, ErrorBlock, Money, money, useLoad, errMsg, fmtDate, words,
} from '../kit';
import { BudgetForecast } from '../workflow/ScClaims';
import { BudgetModal, ServiceModal, GoalModal, ConditionModal, CircumstanceChangeModal, NeedsAssessmentModal } from './modals';
import { COC_ACTION_LABEL, COC_STATUS_LABEL, LinkButton } from './shared';
import ServiceAgreementPanel from './ServiceAgreementPanel';

// Plan & Budgets tab: the service agreement (the dates and hourly rate claims use) first, so the
// Claims Fix list's agreement causes land on it, then one money view (the support coordination
// budget forecast), the plan's budget categories and services it draws on, and the participant's
// goals, needs assessment, health conditions and changes of circumstances.
export default function PlanTab({ detail, onChanged, perms = [] }) {
  // A budget or service edited or deleted anywhere on the tab also reloads the forecast.
  const [signal, setSignal] = useState(0);
  const changed = () => { setSignal((n) => n + 1); onChanged(); };
  return (
    <div className="space-y-5">
      <ServiceAgreementPanel coordinationId={detail.id} perms={perms} onChanged={onChanged} />
      <ForecastPanel detail={detail} onChanged={changed} signal={signal} />
      <PlanBudgets detail={detail} onChanged={changed} />
      <Services detail={detail} onChanged={changed} />
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 items-start">
        <Goals scId={detail.id} />
        <NeedsAssessment scId={detail.id} />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 items-start">
        <Conditions scId={detail.id} />
        <CircumstanceChanges scId={detail.id} />
      </div>
    </div>
  );
}

// Deactivate (budgets, services) and delete (registers) both ask first. Deleted records move to the
// records archive for the retention period; deactivated ones stay visible and stop counting.
function useRecordActions() {
  const confirm = useConfirm();
  const setActive = async (kind, row, makeActive, noun, reload) => {
    if (!makeActive && !await confirm(`Deactivate this ${noun}? It stays on the file, read-only, and stops counting.`, { title: `Deactivate ${noun}`, danger: true, confirmLabel: 'Deactivate' })) return;
    try {
      await api.patch(`/support-coordination/${kind}/${row.id}/deactivate`, { is_active: makeActive });
      toast.success(makeActive ? `The ${noun} is active again` : `The ${noun} is deactivated`);
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (path, row, noun, reload) => {
    if (!await confirm(`Delete this ${noun}? It leaves this file and is kept in the records archive for the retention period.`, { title: `Delete ${noun}`, danger: true, confirmLabel: 'Delete' })) return;
    try { await api.delete(`/support-coordination/${path}/${row.id}`); toast.success(`The ${noun} is deleted`); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return { setActive, remove };
}

// Money left; an overspend says so in words (no sr-only text here: inside a scrolling table an
// absolutely positioned span escapes the scroll box and widens the page on phones).
const Left = ({ value }) => {
  const n = Number(value) || 0;
  if (n < 0) {
    return (
      <span className="inline-flex items-center gap-1 font-semibold tabular-nums whitespace-nowrap text-rose-700">
        <AlertTriangle size={12} aria-hidden />Over by {money(-n)}
      </span>
    );
  }
  return <Money value={value} className="font-semibold text-slate-900 whitespace-nowrap" />;
};

// The forecast's budget lines carry Edit budget and Delete: a line with its own amount is a service
// (its approved budget), the shared line is the plan's support coordination budget.
function ForecastPanel({ detail, onChanged, signal }) {
  const [svc, setSvc] = useState(null);
  const [bud, setBud] = useState(null);
  const { remove } = useRecordActions();
  const actions = (l) => {
    const s = String(l.key).startsWith('svc:') ? (detail.services || []).find((x) => x.id === l.service_id) : null;
    const b = !s && l.budget_id ? (detail.budgets || []).find((x) => x.id === l.budget_id) : null;
    if (s) {
      return [
        { label: 'Edit budget', icon: Pencil, onClick: () => setSvc({ editing: s }) },
        'sep',
        { label: 'Delete', icon: Trash2, danger: true, onClick: () => remove('services', s, 'service', onChanged) },
      ];
    }
    if (b) {
      return [
        { label: 'Edit budget', icon: Pencil, onClick: () => setBud({ editing: b }) },
        'sep',
        { label: 'Delete', icon: Trash2, danger: true, onClick: () => remove('budgets', b, 'budget', onChanged) },
      ];
    }
    return [];
  };
  return (
    <>
      <BudgetForecast coordinationId={detail.id} rowActions={actions} reloadSignal={signal} />
      {svc && <ServiceModal scId={detail.id} editing={svc.editing} onClose={() => setSvc(null)} onSaved={() => { setSvc(null); onChanged(); }} />}
      {bud && <BudgetModal scId={detail.id} editing={bud.editing} onClose={() => setBud(null)} onSaved={() => { setBud(null); onChanged(); }} />}
    </>
  );
}

function PlanBudgets({ detail, onChanged }) {
  const [modal, setModal] = useState(null);
  const { setActive, remove } = useRecordActions();
  const cols = [
    { id: 'name', header: 'Category', accessorFn: (b) => b.budget_name, cell: ({ row: { original: b } }) => (
      <div className="min-w-[180px]">
        <p className="font-semibold text-slate-900 flex flex-wrap items-center gap-1.5">{b.budget_name}{!b.is_active && <Pill tone="slate" size="xs" label="Inactive" />}</p>
        {b.support_category && <p className="text-[12px] text-slate-500">{b.support_category}</p>}
      </div>
    ) },
    { accessorKey: 'budget_amount', header: 'Budget', meta: { align: 'right' }, cell: ({ getValue }) => <Money value={getValue()} /> },
    { accessorKey: 'allocated', header: 'Used', meta: { align: 'right' }, cell: ({ getValue }) => <Money value={getValue()} /> },
    { accessorKey: 'balance', header: 'Left', meta: { align: 'right' }, cell: ({ getValue }) => <Left value={getValue()} /> },
    { accessorKey: 'released', header: 'Released', meta: { align: 'right' }, cell: ({ getValue }) => <Money value={getValue()} className="text-slate-600" /> },
    { id: 'menu', header: '', enableSorting: false, meta: { width: 44 }, cell: ({ row: { original: b } }) => (
      <RowMenu label={`Actions for ${b.budget_name}`} items={[
        { label: 'Edit', icon: Pencil, onClick: () => setModal({ editing: b }) },
        b.is_active ? { label: 'Deactivate', icon: Power, danger: true, onClick: () => setActive('budgets', b, false, 'budget', onChanged) }
          : { label: 'Reactivate', icon: RotateCcw, onClick: () => setActive('budgets', b, true, 'budget', onChanged) },
        'sep',
        { label: 'Delete', icon: Trash2, danger: true, onClick: () => remove('budgets', b, 'budget', onChanged) },
      ]} />
    ) },
  ];
  return (
    <Panel icon={Wallet} title="Plan budgets" sub="The plan’s budget categories that time entries and provider services draw on."
      actions={<LinkButton icon={Plus} onClick={() => setModal({})}>Add budget</LinkButton>}>
      <DataTable columns={cols} data={detail.budgets || []} dense rowClassName={(b) => (!b.is_active ? 'opacity-60' : undefined)}
        empty={<Empty icon={Wallet} title="No plan budgets yet" action={<LinkButton icon={Plus} onClick={() => setModal({})}>Add budget</LinkButton>}>
          Add the plan’s budget categories so notes and services can draw on them.
        </Empty>} />
      {modal && <BudgetModal scId={detail.id} editing={modal.editing} onClose={() => setModal(null)} onSaved={() => { setModal(null); onChanged(); }} />}
    </Panel>
  );
}

const SVC_TONE = { sourcing: 'blue', quoted: 'blue', engaged: 'emerald', completed: 'slate', cancelled: 'slate' };
function Services({ detail, onChanged }) {
  const [modal, setModal] = useState(null);
  const { setActive, remove } = useRecordActions();
  const cols = [
    { id: 'name', header: 'Service', accessorFn: (s) => s.service_name, cell: ({ row: { original: s } }) => (
      <div className="min-w-[200px]">
        <p className="font-semibold text-slate-900 flex flex-wrap items-center gap-1.5">{s.service_name}{!s.is_active && <Pill tone="slate" size="xs" label="Inactive" />}</p>
        {(s.support_item_name || s.support_item_code) && (
          <p className="text-[12px] text-slate-500">{s.support_item_name || 'Support item'}{s.support_item_code ? ` · ${s.support_item_code}` : ''}{s.unit_price ? ` · ${Number(s.unit_price).toLocaleString('en-AU', { style: 'currency', currency: 'AUD' })} a unit` : ''}</p>
        )}
      </div>
    ) },
    { id: 'dates', header: 'Dates', accessorFn: (s) => s.date_from || '', cell: ({ row: { original: s } }) => (
      <span className="whitespace-nowrap text-slate-600">{s.date_from || s.date_to ? `${fmtDate(s.date_from)} – ${fmtDate(s.date_to)}` : '—'}</span>
    ) },
    { accessorKey: 'approved_budget', header: 'Approved', meta: { align: 'right' }, cell: ({ getValue }) => <Money value={getValue()} /> },
    { accessorKey: 'utilised', header: 'Used', meta: { align: 'right' }, cell: ({ getValue }) => <Money value={getValue()} /> },
    { accessorKey: 'balance', header: 'Left', meta: { align: 'right' }, cell: ({ getValue }) => <Left value={getValue()} /> },
    { accessorKey: 'status', header: 'Status', cell: ({ getValue }) => <Pill tone={SVC_TONE[getValue()] || 'slate'} label={words(getValue())} size="xs" /> },
    { id: 'menu', header: '', enableSorting: false, meta: { width: 44 }, cell: ({ row: { original: s } }) => (
      <RowMenu label={`Actions for ${s.service_name}`} items={[
        { label: 'Edit', icon: Pencil, onClick: () => setModal({ editing: s }) },
        s.is_active ? { label: 'Deactivate', icon: Power, danger: true, onClick: () => setActive('services', s, false, 'service', onChanged) }
          : { label: 'Reactivate', icon: RotateCcw, onClick: () => setActive('services', s, true, 'service', onChanged) },
        'sep',
        { label: 'Delete', icon: Trash2, danger: true, onClick: () => remove('services', s, 'service', onChanged) },
      ]} />
    ) },
  ];
  return (
    <Panel icon={ClipboardList} title="Services" sub="Supports being sourced or in place, including the support coordination line claims use."
      actions={<LinkButton icon={Plus} onClick={() => setModal({})}>Add service</LinkButton>}>
      <DataTable columns={cols} data={detail.services || []} dense rowClassName={(s) => (!s.is_active ? 'opacity-60' : undefined)}
        empty={<Empty icon={ClipboardList} title="No services yet" action={<LinkButton icon={Plus} onClick={() => setModal({})}>Add service</LinkButton>}>
          Add the support coordination service with its support item, then the supports you are sourcing.
        </Empty>} />
      {modal && <ServiceModal scId={detail.id} editing={modal.editing} onClose={() => setModal(null)} onSaved={() => { setModal(null); onChanged(); }} />}
    </Panel>
  );
}

const GOAL_TONE = { not_started: 'slate', in_progress: 'blue', achieved: 'emerald', discontinued: 'slate' };
function Goals({ scId }) {
  const l = useLoad(`/support-coordination/${scId}/goals`);
  const [modal, setModal] = useState(null);
  const { remove } = useRecordActions();
  const goals = useMemo(() => l.data || [], [l.data]);
  return (
    <Panel icon={Target} title="Goals" sub={goals.length ? `${goals.filter((g) => g.status === 'achieved').length} of ${goals.length} achieved` : undefined}
      actions={<LinkButton icon={Plus} onClick={() => setModal({})}>Add goal</LinkButton>}>
      {l.error ? <ErrorBlock error={l.error} onRetry={l.reload} /> : !l.data ? <LoadingBlock rows={2} /> : !goals.length ? (
        <p className="text-[13px] text-slate-500">No goals yet. Add the participant’s NDIS goals with how progress is measured.</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {goals.map((g) => (
            <li key={g.id} className="py-3 flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  <p className="text-[13.5px] font-semibold text-slate-900">{g.title}</p>
                  <Pill tone={GOAL_TONE[g.status] || 'slate'} label={words(g.status)} size="xs" />
                  {g.priority === 'high' && <Pill tone="amber" label="High priority" size="xs" />}
                </div>
                <p className="text-[12px] text-slate-500 mt-0.5">{[g.category, g.target_date && `Target ${fmtDate(g.target_date)}`].filter(Boolean).join(' · ')}</p>
                {g.description && <p className="text-[13px] text-slate-700 mt-1">{g.description}</p>}
                {g.measure && <p className="text-[12.5px] text-slate-600 mt-1"><span className="font-semibold">Measure:</span> {g.measure}</p>}
                {g.outcome && <p className="text-[12.5px] text-emerald-800 mt-1"><span className="font-semibold">Outcome:</span> {g.outcome}</p>}
                <div className="flex items-center gap-2 mt-2 max-w-xs">
                  <Meter value={Number(g.progress_pct) || 0} max={100} tone={g.status === 'achieved' ? 'emerald' : 'violet'} label={`${g.title}: ${g.progress_pct || 0}% progress`} />
                  <span className="text-[12px] text-slate-600 tabular-nums">{g.progress_pct || 0}%</span>
                </div>
              </div>
              <RowMenu label={`Actions for goal ${g.title}`} items={[
                { label: 'Edit', icon: Pencil, onClick: () => setModal({ editing: g }) },
                { label: 'Delete', icon: Trash2, danger: true, onClick: () => remove('goals', g, 'goal', l.reload) },
              ]} />
            </li>
          ))}
        </ul>
      )}
      {modal && <GoalModal scId={scId} editing={modal.editing} onClose={() => setModal(null)} onSaved={() => { setModal(null); l.reload(); }} />}
    </Panel>
  );
}

const NA_TONE = { draft: 'slate', submitted: 'blue', approved: 'emerald', declined: 'red' };
const NA_FIELDS = ['assessment_date', 'assessor_name', 'primary_disability', 'diagnosis', 'presenting_needs', 'goals', 'recommended_supports', 'funding_requested'];
function NeedsAssessment({ scId }) {
  const l = useLoad(`/support-coordination/${scId}/needs-assessment`);
  const [open, setOpen] = useState(false);
  const na = l.data || null;
  const empty = !na || !NA_FIELDS.some((k) => na[k]);
  const block = (label, text) => text ? (
    <div key={label}>
      <p className="text-[12px] font-medium text-slate-500">{label}</p>
      <p className="text-[13px] text-slate-700 whitespace-pre-wrap mt-0.5">{text}</p>
    </div>
  ) : null;
  return (
    <Panel icon={FileHeart} title="Needs assessment"
      actions={<LinkButton icon={empty ? Plus : Pencil} onClick={() => setOpen(true)}>{empty ? 'Start' : 'Edit'}</LinkButton>}>
      {l.error ? <ErrorBlock error={l.error} onRetry={l.reload} /> : l.loading && !l.data ? <LoadingBlock rows={2} /> : empty ? (
        <p className="text-[13px] text-slate-500">No needs assessment yet. Record the clinical picture, supports, goals and any funding request.</p>
      ) : (
        <div className="space-y-4">
          {na.funding_status && <Pill tone={NA_TONE[na.funding_status] || 'slate'} label={`Funding ${words(na.funding_status).toLowerCase()}`} />}
          <Facts items={[
            ['Assessed', na.assessment_date ? fmtDate(na.assessment_date) : '—'],
            ['Assessor', na.assessor_name || '—'],
            ['Funding requested', na.funding_requested != null ? <Money value={na.funding_requested} /> : '—'],
            ['Funding approved', na.funding_approved != null ? <Money value={na.funding_approved} /> : '—'],
            na.funding_submitted_date && ['Funding submitted', fmtDate(na.funding_submitted_date)],
            na.review_date && ['Assessment review', fmtDate(na.review_date)],
          ]} />
          {block('Primary disability', na.primary_disability)}
          {block('Diagnosis', na.diagnosis)}
          {block('Presenting needs', na.presenting_needs)}
          {block('Goals', na.goals)}
          {block('Recommended supports', na.recommended_supports)}
          {block('Risk factors', na.risk_factors)}
        </div>
      )}
      {open && <NeedsAssessmentModal scId={scId} na={na} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); l.reload(); }} />}
    </Panel>
  );
}

function Conditions({ scId }) {
  const l = useLoad(`/support-coordination/${scId}/conditions`);
  const [modal, setModal] = useState(null);
  const { remove } = useRecordActions();
  const cols = [
    { accessorKey: 'condition', header: 'Condition', cell: ({ row: { original: c } }) => (
      <div className="min-w-[160px]">
        <p className="font-semibold text-slate-900">{c.condition}</p>
        {c.medications && <p className="text-[12px] text-slate-500">Medications: {c.medications}</p>}
      </div>
    ) },
    { accessorKey: 'diagnosis_date', header: 'Diagnosed', cell: ({ getValue }) => <span className="whitespace-nowrap">{getValue() ? fmtDate(getValue()) : '—'}</span> },
    { accessorKey: 'managing_practitioner', header: 'Practitioner', cell: ({ getValue }) => getValue() || '—' },
    { accessorKey: 'status', header: 'Status', cell: ({ getValue }) => <Pill tone={getValue() === 'active' ? 'blue' : 'slate'} label={words(getValue())} size="xs" /> },
    { id: 'menu', header: '', enableSorting: false, meta: { width: 44 }, cell: ({ row: { original: c } }) => (
      <RowMenu label={`Actions for ${c.condition}`} items={[
        { label: 'Edit', icon: Pencil, onClick: () => setModal({ editing: c }) },
        { label: 'Delete', icon: Trash2, danger: true, onClick: () => remove('conditions', c, 'condition', l.reload) },
      ]} />
    ) },
  ];
  return (
    <Panel icon={Stethoscope} title="Health conditions" actions={<LinkButton icon={Plus} onClick={() => setModal({})}>Add condition</LinkButton>}>
      <DataTable columns={cols} data={l.data || []} loading={l.loading} error={l.error} onRetry={l.reload} dense
        empty={<p className="text-[13px] text-slate-500">No conditions recorded. Add diagnoses, practitioners and medications relevant to coordination.</p>} />
      {modal && <ConditionModal scId={scId} editing={modal.editing} onClose={() => setModal(null)} onSaved={() => { setModal(null); l.reload(); }} />}
    </Panel>
  );
}

const COC_TONE = { draft: 'slate', in_review: 'blue', submitted: 'violet', approved: 'emerald', rejected: 'red', implemented: 'emerald' };
function CircumstanceChanges({ scId }) {
  const l = useLoad(`/support-coordination/${scId}/circumstance-changes`);
  const [modal, setModal] = useState(null);
  const { remove } = useRecordActions();
  const rows = useMemo(() => [...(l.data || [])].sort((a, b) => String(b.change_date || b.created_at || '').localeCompare(String(a.change_date || a.created_at || ''))), [l.data]);
  // A printable "change of details or situation" pack to lodge with the NDIA.
  const exportPack = async (id) => {
    const t = toast.loading('Building the change pack…');
    try {
      const res = await api.get(`/support-coordination/circumstance-changes/${id}/export`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const w = window.open(url, '_blank');
      if (!w) { const a = document.createElement('a'); a.href = url; a.download = 'change-of-circumstances.html'; document.body.appendChild(a); a.click(); a.remove(); }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success('Change pack ready', { id: t });
    } catch (e) { toast.error(errMsg(e, 'The change pack wasn’t built. Try again.'), { id: t }); }
  };
  return (
    <Panel icon={RefreshCw} title="Change of circumstances"
      sub="When the participant’s situation changes, log it here to drive a details update, plan variation or reassessment."
      actions={<LinkButton icon={Plus} onClick={() => setModal({})}>Log change</LinkButton>}>
      {l.error ? <ErrorBlock error={l.error} onRetry={l.reload} /> : !l.data ? <LoadingBlock rows={2} /> : !rows.length ? (
        <p className="text-[13px] text-slate-500">No changes recorded.</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {rows.map((c) => (
            <li key={c.id} className="py-3 flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  <p className="text-[13.5px] font-semibold text-slate-900">{c.title || (c.category ? String(c.category).split(',')[0] : 'Change')}</p>
                  <Pill tone={COC_TONE[c.status] || 'slate'} label={COC_STATUS_LABEL[c.status] || words(c.status)} size="xs" />
                  {(c.urgency === 'high' || c.risk_flag) && <Pill tone="amber" label={c.risk_flag ? 'Risk flagged' : 'Urgent'} size="xs" />}
                </div>
                <p className="text-[12px] text-slate-500 mt-0.5">
                  {[COC_ACTION_LABEL[c.requested_action] || words(c.requested_action), c.change_date && fmtDate(c.change_date), Number(c.evidence_count) > 0 && `${c.evidence_count} evidence`].filter(Boolean).join(' · ')}
                </p>
              </div>
              <RowMenu label="Change actions" items={[
                { label: 'Export the lodgement pack', icon: Download, onClick: () => exportPack(c.id) },
                { label: 'Edit', icon: Pencil, onClick: () => setModal({ editing: c }) },
                'sep',
                { label: 'Delete', icon: Trash2, danger: true, onClick: () => remove('circumstance-changes', c, 'change', l.reload) },
              ]} />
            </li>
          ))}
        </ul>
      )}
      {modal && <CircumstanceChangeModal scId={scId} editing={modal.editing} onExport={exportPack} onClose={() => setModal(null)} onSaved={() => { setModal(null); l.reload(); }} />}
    </Panel>
  );
}
